import {
    type AppUser,
    findUserBy,
    labelForUserKey,
    userDisplay,
} from "../../../services/userService";
import { bearbeiterKeys } from "../../../utils/vorgangScope";
import type { PeopleFilterOption } from "../../redesign/table";

/** A resolved person for an avatar: stable key, full name, short initials/Kürzel. */
export interface Person {
    key: string;
    name: string;
    initials: string;
    /**
     * Kanonische User-Id (an ihr hängt `/users/:id/photo`), `null` wenn der
     * Schlüssel keinem User zugeordnet werden kann. T-85: EINE Quelle für
     * Liste, Vorgangskopf und Picker — vorher reichte `BeteiligteFeld` den
     * Roh-Schlüssel (z. B. „BL") als Id weiter.
     */
    userId: string | null;
}

/**
 * Das anzuzeigende Kürzel einer Person — die EINE Stelle, die entscheidet.
 *
 * Exportiert seit dem 22.09.2026 (PO: „bei den Notizen bitte die Kürzel
 * anzeigen, nicht die eigenen Abbreviations"). Vorher hatte jede Notiz-Ecke
 * ihre eigene Ableitung aus dem Anzeigenamen — vier Kopien, drei davon mit
 * leicht verschiedenem Ergebnis. „Mohamed El Jemai" wurde je nach Kopie zu
 * „ME" oder „MJ"; das gepflegte Kürzel aus dem System sah man nie.
 */
export const kuerzelVon = (u: AppUser | undefined, fallbackName: string): string => {
    if (u) {
        // KMVS-Kürzel = `username` (z. B. „CLL"). VOLLSTÄNDIG übernehmen — dieselbe
        // Quelle wie der Vorgang-Detail-Kopf (kunde-enrichment → foBoLabels nutzt
        // `kuerzel` = username ungekürzt). Früher wurde hier auf ≤4 Zeichen
        // gedeckelt und der Wert kam mangels `/users`-Feld gar nicht an → die
        // Ableitung fiel auf 2-Buchstaben-Namensinitialen zurück („CLL" → „CL"/„C",
        // Review F1). Casing bleibt wie gespeichert (Detail-Kopf zeigt es identisch);
        // ein E-Mail-artiger Wert wird verworfen und nutzt den Namens-Fallback.
        const un = (u.username ?? "").trim();
        if (un && !un.includes("@")) return un;
        const first = (u.firstName ?? "").trim();
        const last = (u.lastName ?? "").trim();
        if (first || last) {
            const i = `${first[0] ?? ""}${last[0] ?? ""}`.trim();
            if (i) return i.toUpperCase();
        }
    }
    const clean = fallbackName.replace(/[^\p{L} ]/gu, "").trim();
    const parts = clean.split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return (clean.slice(0, 2) || "?").toUpperCase();
};

/**
 * Resolve a stored bearbeiter/hauptverantwortlich array (user ids / keys / legacy
 * objects) into displayable people for avatars. Empty in → empty out.
 */
export function resolvePeople(raw: unknown[] | undefined, users: AppUser[]): Person[] {
    return bearbeiterKeys(raw).map((key) => {
        const u = findUserBy(users, key);
        const name = u ? userDisplay(u) : labelForUserKey(key, users);
        return { key, name, initials: kuerzelVon(u, name), userId: u?.id ?? null };
    });
}

/**
 * Kürzel für die People-Filter-Optionen — seit dem 22.09.2026 dieselbe Quelle
 * wie überall sonst.
 *
 * Hier stand eine zweite, abweichende Regel: Kürzel nur bis vier Zeichen,
 * sonst Namens-Initialen, und alles in Großbuchstaben. Dieselbe Person stand
 * damit im Filter als „MAF" und im Vorgang als „MaF" — und wer ein längeres
 * Kürzel hat, tauchte im Filter gar nicht damit auf.
 */
const userTag = (u: AppUser): string => kuerzelVon(u, userDisplay(u));

/**
 * Optionen der People-Spaltenfilter („Beteiligte", „Ersteller") aus dem
 * User-Verzeichnis. Geteilt zwischen der mandantenweiten Vorgangstabelle und
 * dem Vorgänge-Reiter der Kontaktakte, damit in beiden dieselben Namen und
 * Kürzel zur Auswahl stehen.
 */
export function personenFilterOptionen(users: AppUser[]): PeopleFilterOption[] {
    return users.map((u) => ({ id: u.id, label: userDisplay(u), initials: userTag(u) }));
}
